<template>
  <article
    class="build-summary-card"
    :class="{
      'is-current': current,
      'is-expanded': expanded,
      'is-library': variant === 'library',
      'is-compact': variant === 'minimal',
      'is-interactive': interactive && !disabled
    }"
  >
    <div
      class="build-summary-card__summary"
      :role="interactive && !disabled ? 'button' : null"
      :tabindex="interactive && !disabled ? 0 : -1"
      :aria-label="interactive && !disabled ? actionLabel : null"
      @click="activate"
      @keydown.enter.prevent="activate"
      @keydown.space.prevent="activate"
    >
      <span
        class="build-summary-card__thumb"
        :class="{ 'is-empty': !build.image }"
        :style="build.image ? { backgroundImage: 'url(' + build.image + ')' } : null"
        aria-hidden="true"
      >
        <span v-if="!build.image" class="icon icon-tools"></span>
      </span>

      <span class="build-summary-card__body">
        <span class="build-summary-card__title-row">
          <span class="build-summary-card__name" :title="build.name">{{ build.name }}</span>
          <span class="build-summary-card__status" :class="statusToneClass">{{ status }}</span>
        </span>

        <span class="build-summary-card__meta">
          <span>{{ selectedCount }} of {{ totalParts }} core parts</span>
          <span v-if="build.missingCount">{{ build.missingCount }} missing price{{ build.missingCount === 1 ? '' : 's' }}</span>
          <span v-if="build.unavailableCount" class="is-warning">
            <span class="icon icon-warning" aria-hidden="true"></span>
            {{ build.unavailableCount }} unavailable
          </span>
        </span>

        <span v-if="variant !== 'minimal' && highlights.length" class="build-summary-card__highlights">
          <span v-for="highlight in highlights" :key="highlight.key">
            <small>{{ highlight.kind }}</small>
            <span :title="highlight.name">{{ highlight.name }}</span>
          </span>
        </span>
        <span v-else-if="variant !== 'minimal'" class="build-summary-card__empty-copy">
          Add a processor or graphics card to define this build.
        </span>

        <span v-if="variant !== 'minimal'" class="build-summary-card__footer">
          <span class="build-summary-card__progress-wrap">
            <span class="build-summary-card__progress-meta">
              <span>Completion</span>
              <span>{{ progress }}%</span>
            </span>
            <span class="build-summary-card__progress" aria-hidden="true">
              <span :style="{ width: progress + '%' }"></span>
            </span>
          </span>

          <span class="build-summary-card__metrics">
            <span v-if="showPerformance && build.performance !== undefined && build.performance !== null">
              <strong :class="build.performanceClass">{{ performanceLabel }}</strong>
              <small>performance</small>
            </span>
            <span v-if="showPrices && build.hasPrice">
              <strong><span class="build-summary-card__currency">$</span>{{ build.totalPrice }}</strong>
              <small>estimated total</small>
            </span>
            <span v-if="build.tdp">
              <strong>{{ build.tdp }}<small> W</small></strong>
              <small>estimated draw</small>
            </span>
          </span>
        </span>
      </span>

      <div class="build-summary-card__actions" @click.stop @keydown.stop>
        <slot name="actions">
          <span v-if="!disabled" class="build-summary-card__arrow" aria-hidden="true">&rsaquo;</span>
          <span v-else class="build-summary-card__current-mark icon icon-check" aria-hidden="true"></span>
        </slot>
      </div>
    </div>

    <div v-if="$slots.default" class="build-summary-card__details">
      <slot></slot>
    </div>
  </article>
</template>

<script>
export default {
  name: 'BuildSummaryCard',
  props: {
    build: {
      type: Object,
      required: true
    },
    totalParts: {
      type: Number,
      default: 7
    },
    status: {
      type: String,
      default: 'In progress'
    },
    statusTone: {
      type: String,
      default: ''
    },
    variant: {
      type: String,
      default: 'compact'
    },
    interactive: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    current: {
      type: Boolean,
      default: false
    },
    dirty: {
      type: Boolean,
      default: false
    },
    expanded: {
      type: Boolean,
      default: false
    },
    showPrices: {
      type: Boolean,
      default: true
    },
    showPerformance: {
      type: Boolean,
      default: false
    },
    actionLabel: {
      type: String,
      default: 'Open build'
    }
  },
  computed: {
    selectedCount () {
      const value = this.build.selectedCount !== undefined ? this.build.selectedCount : this.build.partCount
      return Math.max(0, Number(value) || 0)
    },
    progress () {
      if (this.build.progress !== undefined && this.build.progress !== null) {
        return Math.max(0, Math.min(100, Math.round(Number(this.build.progress) || 0)))
      }
      return this.totalParts ? Math.round(this.selectedCount / this.totalParts * 100) : 0
    },
    highlights () {
      if (Array.isArray(this.build.highlights) && this.build.highlights.length) {
        return this.build.highlights.slice(0, 4)
      }
      const values = []
      if (this.build.cpu) values.push({ key: 'cpu', kind: 'CPU', name: this.build.cpu })
      if (this.build.gpu) values.push({ key: 'gpu', kind: 'GPU', name: this.build.gpu })
      return values
    },
    statusToneClass () {
      if (this.dirty) return 'is-warning'
      if (this.build.complete) return 'is-complete'
      if (this.statusTone) return 'is-' + this.statusTone
      return ''
    },
    performanceLabel () {
      const score = Number(this.build.performance) || 0
      return score >= 100 ? 'High end' : score + '/100'
    }
  },
  methods: {
    activate () {
      if (!this.interactive || this.disabled) return
      this.$emit('activate', this.build)
    }
  }
}
</script>

<style scoped>
.build-summary-card {
  min-width: 0;
  overflow: hidden;
  border: 1px solid rgba(70, 82, 105, 0.58);
  border-radius: 9px;
  background:
    radial-gradient(circle at 8% 0%, rgba(25, 151, 198, 0.08), transparent 34%),
    linear-gradient(145deg, rgba(12, 17, 26, 0.78), rgba(7, 10, 16, 0.7));
  color: #cfd6e1;
  transition: border-color 0.16s ease, background 0.16s ease, transform 0.16s ease;
}

.build-summary-card.is-interactive:hover,
.build-summary-card.is-interactive:focus-within,
.build-summary-card.is-current {
  border-color: rgba(37, 175, 225, 0.62);
  background:
    radial-gradient(circle at 8% 0%, rgba(25, 151, 198, 0.13), transparent 36%),
    linear-gradient(145deg, rgba(13, 27, 38, 0.82), rgba(7, 12, 19, 0.72));
}

.build-summary-card.is-interactive:hover {
  transform: translateY(-1px);
}

.build-summary-card__summary {
  display: grid;
  grid-template-columns: 58px minmax(0, 1fr) auto;
  gap: 0.85rem;
  align-items: start;
  min-height: 132px;
  padding: 0.85rem;
  outline: none;
}

.build-summary-card__summary[role="button"] {
  cursor: pointer;
}

.build-summary-card__summary:focus-visible {
  box-shadow: inset 0 0 0 2px rgba(37, 175, 225, 0.5);
}

.build-summary-card__thumb {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 58px;
  height: 58px;
  overflow: hidden;
  border: 1px solid rgba(93, 107, 131, 0.68);
  border-radius: 8px;
  background-color: #e7ecf2;
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  color: #32b8e5;
}

.build-summary-card__thumb.is-empty {
  background:
    radial-gradient(circle at 50% 35%, rgba(32, 174, 222, 0.16), transparent 52%),
    rgba(7, 14, 22, 0.82);
}

.build-summary-card__thumb .icon {
  font-size: 21px;
}

.build-summary-card__body,
.build-summary-card__title-row,
.build-summary-card__meta,
.build-summary-card__highlights,
.build-summary-card__footer,
.build-summary-card__metrics {
  min-width: 0;
}

.build-summary-card__body {
  display: block;
}

.build-summary-card__title-row {
  display: flex;
  align-items: center;
  gap: 0.55rem;
}

.build-summary-card__name {
  flex: 0 1 auto;
  overflow: hidden;
  color: #e5eaf2;
  font-size: 15px;
  font-weight: 650;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.build-summary-card__status {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  min-height: 18px;
  padding: 0.06rem 0.4rem;
  border: 1px solid rgba(86, 100, 125, 0.52);
  border-radius: 999px;
  background: rgba(86, 100, 125, 0.08);
  color: #9ca8ba;
  font-size: 8px;
  font-weight: 800;
  letter-spacing: 0.045em;
  line-height: 1;
  text-transform: uppercase;
  white-space: nowrap;
}

.build-summary-card__status.is-complete {
  border-color: rgba(27, 201, 142, 0.42);
  background: rgba(27, 201, 142, 0.08);
  color: #65d5ad;
}

.build-summary-card__status.is-warning {
  border-color: rgba(224, 184, 95, 0.44);
  background: rgba(224, 184, 95, 0.08);
  color: #e0b85f;
}

.build-summary-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 0.55rem;
  margin-top: 0.3rem;
  color: #78869a;
  font-size: 9.5px;
  line-height: 1.25;
}

.build-summary-card__meta .is-warning {
  color: #d9ad59;
}

.build-summary-card__meta .icon {
  margin-right: 2px;
}

.build-summary-card__highlights {
  display: grid;
  gap: 0.18rem;
  margin-top: 0.48rem;
}

.build-summary-card__highlights > span {
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr);
  gap: 0.45rem;
  min-width: 0;
  color: #a8b2c1;
  font-size: 10.5px;
  line-height: 1.25;
}

.build-summary-card__highlights small {
  color: #657389;
  font-size: 8px;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.build-summary-card__highlights > span > span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.build-summary-card__empty-copy {
  display: block;
  margin-top: 0.55rem;
  color: #748095;
  font-size: 10.5px;
  line-height: 1.35;
}

.build-summary-card__footer {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 0.65rem;
}

.build-summary-card__progress-wrap {
  display: block;
  width: min(148px, 42%);
}

.build-summary-card__progress-meta {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
  color: #748095;
  font-size: 8px;
  font-weight: 750;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.build-summary-card__progress {
  display: block;
  height: 3px;
  overflow: hidden;
  border-radius: 10px;
  background: #2c3443;
}

.build-summary-card__progress > span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #1997c6, #28c0ec);
}

.build-summary-card__metrics {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 0.9rem;
}

.build-summary-card__metrics > span {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  min-width: 0;
}

.build-summary-card__metrics strong {
  color: #d4dbe6;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.1;
  white-space: nowrap;
}

.build-summary-card__metrics strong > small {
  color: inherit;
  font-size: 0.82em;
}

.build-summary-card__metrics > span > small {
  margin-top: 0.12rem;
  color: #677388;
  font-size: 7px;
  font-weight: 750;
  letter-spacing: 0.055em;
  text-transform: uppercase;
}

.build-summary-card__currency {
  margin-right: 2px;
  color: #7f8b9e;
}

.build-summary-card__metrics .score-high,
.build-summary-card__metrics .score-good {
  color: #1bc98e;
}

.build-summary-card__metrics .score-mid {
  color: #c5b95a;
}

.build-summary-card__metrics .score-low {
  color: #c95e71;
}

.build-summary-card__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.45rem;
}

.build-summary-card__arrow {
  color: #4fbfe5;
  font-size: 25px;
  font-weight: 300;
  line-height: 1;
  transition: color 0.16s ease, transform 0.16s ease;
}

.build-summary-card.is-interactive:hover .build-summary-card__arrow {
  color: #9ce7ff;
  transform: translateX(2px);
}

.build-summary-card__current-mark {
  color: #54d5ad;
  font-size: 14px;
}

.build-summary-card__details {
  border-top: 1px solid rgba(70, 82, 105, 0.58);
}

.build-summary-card.is-library .build-summary-card__summary {
  grid-template-columns: 94px minmax(0, 1fr) auto;
  gap: 0.9rem;
  min-height: 164px;
  padding: 0.9rem 1rem;
}

.build-summary-card.is-library .build-summary-card__thumb {
  width: 94px;
  height: 94px;
  box-shadow:
    inset 0 0 20px rgba(17, 24, 38, 0.12),
    0 8px 22px rgba(0, 0, 0, 0.2);
}

.build-summary-card.is-library .build-summary-card__name {
  font-size: 20px;
  font-weight: 600;
}

.build-summary-card.is-library .build-summary-card__status {
  min-height: 19px;
  padding-inline: 0.45rem;
  font-size: 8.5px;
}

.build-summary-card.is-library .build-summary-card__meta {
  margin-top: 0.35rem;
  font-size: 10.5px;
}

.build-summary-card.is-library .build-summary-card__highlights {
  grid-template-columns: repeat(2, minmax(220px, 305px));
  gap: 0.34rem 2rem;
  width: min(100%, 642px);
  margin-top: 0.7rem;
}

.build-summary-card.is-library .build-summary-card__highlights > span {
  grid-template-columns: 50px minmax(0, 1fr);
  gap: 0.5rem;
  font-size: 12px;
  line-height: 1.3;
}

.build-summary-card.is-library .build-summary-card__highlights small {
  font-size: 8.5px;
}

/* The library has room for a proper information band. Completion and the
   three build readings share one rhythm instead of floating at opposite ends. */
.build-summary-card.is-library .build-summary-card__footer {
  display: grid;
  grid-template-columns: minmax(180px, 1.15fr) minmax(0, 1.85fr);
  gap: 1rem;
  align-items: center;
  margin-top: 0.72rem;
  padding: 0.62rem 0.72rem;
  border: 1px solid rgba(70, 82, 105, 0.32);
  border-radius: 6px;
  background: rgba(8, 14, 22, 0.42);
}

.build-summary-card.is-library .build-summary-card__progress-wrap {
  width: auto;
}

.build-summary-card.is-library .build-summary-card__metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(88px, 1fr));
  gap: 0;
  align-items: center;
}

.build-summary-card.is-library .build-summary-card__metrics > span {
  align-items: flex-start;
  min-height: 29px;
  padding-left: 1rem;
  border-left: 1px solid rgba(70, 82, 105, 0.42);
}

.build-summary-card.is-library .build-summary-card__metrics strong {
  font-size: 13px;
}

.build-summary-card.is-library .build-summary-card__metrics > span > small {
  font-size: 7.5px;
}

@media (max-width: 980px) {
  .build-summary-card.is-library .build-summary-card__highlights {
    grid-template-columns: 1fr;
    width: 100%;
  }

  .build-summary-card.is-library .build-summary-card__footer {
    grid-template-columns: 1fr;
    gap: 0.65rem;
  }

  .build-summary-card.is-library .build-summary-card__metrics > span:first-child {
    padding-left: 0;
    border-left: 0;
  }
}

@media (max-width: 760px) {
  .build-summary-card__summary,
  .build-summary-card.is-library .build-summary-card__summary {
    grid-template-columns: 54px minmax(0, 1fr);
    gap: 0.7rem;
    min-height: 0;
    padding: 0.75rem;
  }

  .build-summary-card__thumb,
  .build-summary-card.is-library .build-summary-card__thumb {
    width: 54px;
    height: 54px;
  }

  .build-summary-card__actions {
    grid-column: 1 / -1;
    justify-content: flex-end;
  }

  .build-summary-card.is-library .build-summary-card__highlights {
    grid-template-columns: 1fr;
  }

  .build-summary-card.is-library .build-summary-card__footer {
    display: flex;
    padding-top: 0.6rem;
  }

  .build-summary-card.is-library .build-summary-card__metrics {
    display: flex;
  }

  .build-summary-card.is-library .build-summary-card__metrics > span {
    min-height: 0;
    padding-left: 0;
    border-left: 0;
  }

  .build-summary-card__footer {
    align-items: stretch;
    flex-direction: column;
    gap: 0.55rem;
  }

  .build-summary-card__progress-wrap {
    width: 100%;
  }

  .build-summary-card__metrics {
    justify-content: flex-start;
  }

  .build-summary-card__metrics > span {
    align-items: flex-start;
  }
}
.build-summary-card.is-compact .build-summary-card__summary { grid-template-columns: 38px minmax(0, 1fr) auto; min-height: 0; padding: 12px 14px; gap: 12px; }
.build-summary-card.is-compact .build-summary-card__thumb { width: 38px; height: 38px; flex-basis: 38px; }
.build-summary-card.is-compact .build-summary-card__actions { grid-column: auto; align-self: center; }
.build-summary-card.is-compact .build-summary-card__name { font-size: 13px; }
</style>
